/**
 * Form yanıt verisi işleme ve dönüştürme yardımcı fonksiyonları
 */

/**
 * Form verisini parse et
 */
export const parseFormData = (data) => {
  try {
    if (typeof data === 'string') {
      // Önce dıştaki tırnak işaretlerini kaldır
      let cleanString = data.replace(/^"(.*)"$/, '$1');
      // Escape edilmiş karakterleri düzelt
      cleanString = cleanString.replace(/\\"/g, '"');
      // String'i döndür
      return cleanString;
    }
    // Eğer data string değilse, string'e çevir
    return String(data);
  } catch (error) {
    console.error('Form verisi parse edilirken hata:', error);
    console.error('Parse edilemeyen veri:', data);
    return '';
  }
};

/**
 * Form alanlarını kullanıcı değerleriyle birleştir
 */
export const mergeFieldsWithValues = (formContentObj, formDataObj) => {
  return (formContentObj.fields || []).map(field => {
    const fieldData = formDataObj.formData?.[field.id];
    let fieldValue = '';
    
    if (field.type === 'file') {
      // Öncelik: files > formData.value
      let fileVal = formDataObj.files?.[field.id];
      if (!fileVal || (Array.isArray(fileVal) && fileVal.length === 0)) {
        if (fieldData?.value) {
          fileVal = fieldData.value;
        }
      }
      fieldValue = fileVal || [];
    } else if (field.type === 'signature') {
      fieldValue = formDataObj.signature || '';
    } else if (field.type === 'calculation') {
      try {
        if (typeof fieldData?.value === 'string') {
          fieldValue = JSON.parse(fieldData.value);
        } else if (typeof fieldData?.value === 'object') {
          fieldValue = fieldData.value;
        } else {
          fieldValue = {};
        }
      } catch (error) {
        console.error('Hesaplama alanı parse hatası:', error);
        fieldValue = {};
      }
    } else if (field.type === 'name') {
      fieldValue = fieldData?.value || '';
      if (typeof fieldValue === 'object') {
        fieldValue = fieldValue.value || `${fieldValue.firstName || ''} ${fieldValue.lastName || ''}`.trim();
      }
    } else {
      fieldValue = fieldData?.value || '';
    }
    
    return {
      ...field,
      value: fieldValue,
      label: field.label || '',
      type: field.type || 'text',
      options: field.options || [],
      required: field.required || false,
      placeholder: field.placeholder || '',
      helpText: field.helpText || '',
      size: field.size || '12'
    };
  });
};

/**
 * Form yanıtlarını filtrele
 */
export const filterResponses = (responses, { startDate, endDate, searchTerm, folders }) => {
  let filtered = [...responses];

  // Tarih aralığı filtresi
  if (startDate && endDate) {
    filtered = filtered.filter(response => {
      const responseDate = new Date(response.createDate);
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59); // Bitiş tarihini günün sonuna ayarla
      return responseDate >= start && responseDate <= end;
    });
  }

  // Arama filtresi
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase();
    filtered = filtered.filter(response => {
      // Klasör isimlerini al
      let folderNames = '';
      if (response.parentId) {
        const parentIds = response.parentId.split(',').map(id => id.trim()).filter(id => id !== '');
        const foundFolders = parentIds
          .map(id => folders[id])
          .filter(folderName => folderName)
          .filter((folderName, index, arr) => arr.indexOf(folderName) === index); // Tekrarları kaldır
        folderNames = foundFolders.join(' ').toLowerCase();
      }
      
      return response.title?.toLowerCase().includes(searchLower) ||
             response.user?.toLowerCase().includes(searchLower) ||
             response.formId?.toString().includes(searchLower) ||
             folderNames.includes(searchLower);
    });
  }

  return filtered;
};

/**
 * Kullanıcı bilgilerini formatla
 */
export const formatUserInfo = (userData) => {
  return {
    id: userData?.id,
    name: userData?.userData?.name || userData?.username || 'Kullanıcı',
    email: userData?.userData?.email || userData?.username || '',
    categories: userData?.userData?.categories,
    isActive: userData?.userData?.isActive !== false,
    token: userData?.userData?.token || userData?.token
  };
};

/**
 * Form güncelleme verisi hazırla
 */
export const prepareFormUpdateData = (editingResponse, updatedFields, userInfo) => {
  // Form alanlarını hazırla
  const formFields = updatedFields.map(field => {
    let fieldValue = field.value;
    
    // İsim alanı için özel işlem
    if (field.type === 'name') {
      fieldValue = {
        firstName: field.value?.firstName || '',
        lastName: field.value?.lastName || ''
      };
    }
    
    // İmza alanı için özel işlem
    if (field.type === 'signature') {
      fieldValue = field.value || null;
    }

    return {
      ...field,
      value: fieldValue
    };
  });

  const formContentObj = {
    id: parseInt(editingResponse.formId),
    title: editingResponse.formTitle,
    description: editingResponse.formDescription,
    fields: formFields.map(field => ({
      id: field.id,
      type: field.type,
      label: field.label,
      required: field.required || false,
      placeholder: field.placeholder || '',
      helpText: field.helpText || '',
      size: field.size || '12',
      options: field.options || []
    })),
    settings: editingResponse.settings || {},
    isPublished: true,
    publishedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    formLink: '',
    logo: ''
  };

  const formDataObj = {
    formData: formFields.reduce((acc, field) => {
      acc[field.id] = {
        value: field.value,
        label: field.label || '',
        type: field.type || 'text'
      };
      return acc;
    }, {}),
    signature: editingResponse.signature || null,
    files: editingResponse.files || {}
  };

  return {
    id: editingResponse.id || 0,
    formId: editingResponse.formId?.toString() || '',
    formContent: JSON.stringify(formContentObj),
    formData: JSON.stringify(formDataObj),
    userId: userInfo.id || 0,
    user: userInfo.name || userInfo.email || 'Kullanıcı'
  };
};

/**
 * PDF export için form verisi hazırla
 */
export const prepareFormDataForPDF = (record) => {
  // API'den gelen verileri parse et
  let formContentObj = {};
  let formDataObj = {};

  try {
    if (typeof record.formContent === 'string') {
      formContentObj = JSON.parse(record.formContent);
    } else if (typeof record.formContent === 'object') {
      formContentObj = record.formContent;
    }

    if (typeof record.formData === 'string') {
      formDataObj = JSON.parse(record.formData);
    } else if (typeof record.formData === 'object') {
      formDataObj = record.formData;
    }
  } catch (error) {
    console.error('Veri parse edilirken hata:', error);
    formContentObj = {};
    formDataObj = {};
  }

  // Form alanlarını kullanıcı değerleriyle birleştir
  const fieldsWithValues = formContentObj.fields.map(field => {
    const fieldData = formDataObj.formData?.[field.id];
    
    let fieldValue = '';
    if (field.type === 'file') {
      fieldValue = formDataObj.files?.[field.id] || [];
    } else if (field.type === 'signature') {
      fieldValue = fieldData?.value || formDataObj.signature || '';
    } else if (field.type === 'calculation') {
      try {
        if (typeof fieldData?.value === 'string') {
          fieldValue = JSON.parse(fieldData.value);
        } else if (typeof fieldData?.value === 'object') {
          fieldValue = fieldData.value;
        } else {
          fieldValue = {};
        }
      } catch (error) {
        console.error('Hesaplama alanı parse hatası:', error);
        fieldValue = {};
      }
    } else {
      fieldValue = fieldData?.value || '';
    }
    
    return {
      ...field,
      value: fieldValue,
      label: fieldData?.label || field.label || '',
      type: field.type || 'text'
    };
  });

  return {
    id: record.id,
    formId: formContentObj.id,
    formTitle: formContentObj.title || '',
    formDescription: formContentObj.description || '',
    formFields: fieldsWithValues,
    settings: {
      ...formContentObj.settings,
      documentControlEnabled: formContentObj.settings?.documentControlEnabled || false,
      documentPrefix: record.documentPrefix || formContentObj.settings?.documentPrefix || '',
      documentCode: record.documentCode || formContentObj.settings?.documentCode || '',
      department: record.department || formContentObj.settings?.department || ''
    },
    signature: formDataObj.signature || null,
    files: formDataObj.files || {},
    createDate: record.createDate,
    editDate: record.editDate,
    user: record.user
  };
};
